import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const MultilingualSelector: React.FC = () => {
  const { currentLanguage, setLanguage, t, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Găsim limba curentă
  const currentLang = availableLanguages.find(lang => lang.code === currentLanguage) || availableLanguages[0];

  // Handler click în afara dropdown pentru a-l închide
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (languageCode: string) => {
    const language = availableLanguages.find(lang => lang.code === languageCode);
    if (!language?.active) return;
    
    setLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left w-full" ref={dropdownRef}>
      <button
        type="button"
        className="w-full justify-center inline-flex items-center px-6 py-3 bg-white text-blue-800 font-bold rounded-lg border-2 border-blue-300 hover:bg-blue-50 focus:outline-none shadow-lg"
        style={{ minHeight: '48px' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="mr-2 text-lg">{currentLang.flag}</span>
        <span className="text-blue-800 font-semibold">{currentLang.name}</span>
        <svg 
          className={`ml-2 h-4 w-4 text-blue-800 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center p-4"
          style={{ zIndex: 50000 }}
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl border-2 border-blue-200 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div className="py-2">
              <div className="px-4 py-3 text-center text-gray-800 font-bold border-b border-gray-200 bg-blue-50 rounded-t-lg">
                🌍 {t('language.select')}
              </div>

              <div className="max-h-60 overflow-y-auto">
                {availableLanguages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageSelect(language.code)}
                    className={`w-full flex items-center px-4 py-3 text-left hover:bg-blue-50 transition-colors ${
                      language.active ? 'text-gray-800 cursor-pointer' : 'text-gray-400 cursor-not-allowed bg-gray-50'
                    } ${language.code === currentLanguage ? 'bg-blue-100 font-semibold' : ''}`}
                    disabled={!language.active}
                  >
                    <span className="mr-3 text-xl">{language.flag}</span>
                    <span className="flex-1 font-medium">{language.name}</span>
                    {language.code === currentLanguage && (
                      <span className="text-blue-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                    {!language.active && (
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">{t('language.comingSoon')}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <a
                href="#"
                className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{t('language.help')}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultilingualSelector; 